import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNotify, useRefresh } from 'react-admin';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { adminApi } from '../../providers/dataProvider';
import { USAGE_FEATURES } from '../../constants';
import { useSharedAllowance } from '../../hooks/useSharedAllowance';
import { useAdminText } from '../shared/useAdminText';

export function GrantBonusDialog({ userId, onClose }: { userId: string; onClose: () => void }) {
  const t = useAdminText(), notify = useNotify(), refresh = useRefresh();
  const [feature, setFeature] = useState('ai_chat_message');
  const [amount, setAmount] = useState('');
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [conflict, setConflict] = useState(false);
  const shared = feature === 'shared_elisa_replies';
  const allowance = useSharedAllowance(userId, shared);
  const mutation = useMutation({ retry: false, networkMode: 'always',
    mutationFn: async () => {
      if (shared) {
        if (!allowance.data || allowance.isError) throw new Error('Shared allowance unavailable');
        return adminApi.adjustSharedAllowance(userId, { pairId: allowance.data.pairId,
          version: allowance.data.version, amount: Number(amount), requestId });
      }
      return adminApi.grantBonusUsage(userId, feature, Number(amount), requestId);
    },
    onSuccess: () => { refresh(); notify(t('bonusSaved', 'Bonus saved'), { type: 'success' }); onClose(); },
    onError: (error) => {
      if ((error as { statusCode?: number })?.statusCode === 409) setConflict(true);
      if (shared) void allowance.refetch();
    },
  });
  const valid = Number.isInteger(Number(amount)) && Number(amount) >= 1 && Number(amount) <= 10000;
  return <Dialog open onClose={() => { if (!mutation.isPending) onClose(); }} fullWidth maxWidth="xs">
    <DialogTitle>{t('grantBonusTitle', 'Grant Bonus Usage')}</DialogTitle>
    <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
      <Typography variant="body2" color="text.secondary">{t('bonusExplanation', 'Add extra usage above the plan allowance. Manage existing bonuses in View usage.')}</Typography>
      <TextField select label={t('bonusFeature', 'Feature')} value={feature} disabled={mutation.isPending}
        onChange={event => { setFeature(event.target.value); setRequestId(crypto.randomUUID()); mutation.reset(); }}>
        {USAGE_FEATURES.map(item => <MenuItem key={item.value} value={item.value}>{t(`feature.${item.value}`, item.label)}</MenuItem>)}
        <MenuItem value="shared_elisa_replies">{t('sharedElisa', 'Shared Elisa replies')}</MenuItem>
      </TextField>
      {shared && <>
        <Typography variant="body2">{t('pairBonusScope', 'One weekly allowance for both partners. This bonus stays with this couple, including if they reconnect; it never transfers to a different partner.')}</Typography>
        {allowance.isError ? <Alert severity="error" action={<Button onClick={() => void allowance.refetch()}>{t('retry', 'Retry')}</Button>}>{t('sharedAllowanceFailed', 'Could not load the shared allowance.')}</Alert>
          : allowance.isPending ? <Typography>{t('loading', 'Loading…')}</Typography>
          : allowance.data === null ? <Alert severity="info">{t('noPartnerBonus', 'Link a partner before granting shared replies.')}</Alert>
          : allowance.data && <Typography variant="body2">{t('sharedBonusCurrent', 'Current recurring bonus: %{bonus} replies per week', { bonus: allowance.data.bonus })}</Typography>}
      </>}
      <TextField label={t('bonusAmount', 'Amount')} type="number" value={amount} disabled={mutation.isPending}
        onChange={event => { setAmount(event.target.value); setRequestId(crypto.randomUUID()); mutation.reset(); }}
        slotProps={{ htmlInput: { min: 1, max: 10000, step: 1 } }}
        helperText={feature === 'memory_photos' ? t('photoBonusHelp', 'Permanent personal photo slots, until revoked (1–10000).') : t('usageBonusHelp', 'Extra uses each reset period (1–10000).')} />
      {conflict ? <Alert severity="error">{t('bonusConflict', 'The couple or bonus changed. Close this dialog and review the current allowance before granting again.')}</Alert>
        : mutation.isError && <Alert severity="error">{t('bonusSaveFailed', 'Could not confirm the bonus. Review the current allowance before retrying.')}</Alert>}
    </Stack></DialogContent>
    <DialogActions><Button disabled={mutation.isPending} onClick={onClose}>{t('cancel', 'Cancel')}</Button>
      <Button variant="contained" disabled={!valid || conflict || mutation.isPending || (shared && (!allowance.data || allowance.isFetching || allowance.isError))}
        onClick={() => mutation.mutate()}>{mutation.isPending ? t('saving', 'Saving…') : t('grantBonus', 'Grant Bonus')}</Button></DialogActions>
  </Dialog>;
}
